import { RefObject, useRef } from "react";

import {
  useEventListener,
  useListKeyboardNavigation,
} from "@tsmono/react/hooks";
import type { VirtualListHandle } from "@tsmono/react/virtual";
import { isEditableTarget } from "@tsmono/util";

interface TranscriptKeyboardNavigationOptions {
  listHandle: RefObject<VirtualListHandle | null>;
  scrollRef?: RefObject<HTMLDivElement | null>;
  /** Total number of items in the list. */
  itemCount: number;
  /** `j` — scroll to the next turn (down, vim-style). */
  onNext?: () => void;
  /** `k` — scroll to the previous turn (up, vim-style). */
  onPrev?: () => void;
  /** `h` — move to the previous agent lane (left, vim-style). */
  onPrevAgent?: () => void;
  /** `l` — move to the next agent lane (right, vim-style). */
  onNextAgent?: () => void;
  /** `f` — open focus mode for the current turn (the one the scroll
   *  tracker reports at the top of the viewport). */
  onFocusCurrentTurn?: () => void;
  /** `gg` — jump to the first turn (vim-style). */
  onFirst?: () => void;
  /** `G` — jump to the last turn (vim-style). */
  onLast?: () => void;
  /**
   * When true, all shortcuts stand down so another surface can own the
   * keyboard (e.g. find-in-page is open — its keys, including a post-blur
   * `g`/`j`, must reach the find box rather than navigate the list).
   */
  disabled?: boolean;
}

/**
 * Transcript keyboard navigation: the vim-style turn/agent keys (j/k, h/l,
 * gg/G) layered on top of the generic list navigation (modifier+arrows /
 * Home/End from `useListKeyboardNavigation`). Transcript-specific on purpose —
 * the generic hook stays free of turn/agent semantics.
 */
export function useTranscriptKeyboardNavigation({
  listHandle,
  scrollRef,
  itemCount,
  onNext,
  onPrev,
  onPrevAgent,
  onNextAgent,
  onFocusCurrentTurn,
  onFirst,
  onLast,
  disabled,
}: TranscriptKeyboardNavigationOptions): void {
  useListKeyboardNavigation({ listHandle, scrollRef, itemCount, disabled });

  // Timestamp of the last lone `g`, for recognizing the `gg` two-stroke.
  const lastGTimeRef = useRef(0);
  useEventListener(
    window,
    "keydown",
    (event) => {
      if (disabled) return;
      // Several transcripts can be mounted at once (e.g. a reference-preview
      // popover over the main view). Only the visible one may claim these
      // window-capture keys — otherwise a hidden/offscreen instance swallows
      // them via stopImmediatePropagation. Mirrors GoToTurnBar's gate.
      const container = scrollRef?.current;
      if (
        container &&
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- intentional: checkVisibility is absent in jsdom and older browsers
        (!container.isConnected || container.checkVisibility?.() === false)
      ) {
        return;
      }
      const hasModifier = event.metaKey || event.ctrlKey || event.altKey;

      // The `gg` chord means two discrete `g` STROKES: a held key's auto-repeat
      // must not complete it (vim ignores repeat here too), and any other key
      // between the two g's cancels the pending first stroke.
      if (event.key !== "g") lastGTimeRef.current = 0;
      if (event.repeat && (event.key === "g" || event.key === "G")) return;

      // `j` / `k` step between turns. Plain keys, so ignore them while a
      // modifier is held (don't swallow browser/OS chords) or while typing.
      if (!hasModifier && (event.key === "j" || event.key === "k")) {
        if (!onNext && !onPrev) return;
        if (isEditableTarget(document.activeElement)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        // vim-style: j = down (next turn), k = up (previous turn).
        if (event.key === "j") onNext?.();
        else onPrev?.();
        return;
      }

      // `h` / `l` step between agent lanes (subagents). Same guards as j/k.
      // (ArrowLeft / ArrowRight — prev/next sample/sibling — is NOT bound
      // here: the nav control that advertises it in its tooltip owns the
      // binding, so tooltip and wiring cannot desync.)
      if (!hasModifier && (event.key === "h" || event.key === "l")) {
        if (!onPrevAgent && !onNextAgent) return;
        if (isEditableTarget(document.activeElement)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        // vim-style: h = left (previous agent), l = right (next agent).
        if (event.key === "h") onPrevAgent?.();
        else onNextAgent?.();
        return;
      }

      // `f` opens focus mode for the current turn. Same guards as j/k.
      if (!hasModifier && event.key === "f") {
        if (!onFocusCurrentTurn) return;
        if (isEditableTarget(document.activeElement)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        onFocusCurrentTurn();
        return;
      }

      // `G` jumps to the last turn; `gg` (double-tap g) to the first.
      if (!hasModifier && (event.key === "g" || event.key === "G")) {
        if (!onFirst && !onLast) return;
        if (isEditableTarget(document.activeElement)) return;
        if (event.key === "G") {
          event.preventDefault();
          event.stopImmediatePropagation();
          lastGTimeRef.current = 0;
          onLast?.();
          return;
        }
        // lowercase g: second g within the window fires gg → first turn.
        const now = Date.now();
        if (now - lastGTimeRef.current < 500) {
          event.preventDefault();
          event.stopImmediatePropagation();
          lastGTimeRef.current = 0;
          onFirst?.();
        } else {
          lastGTimeRef.current = now;
        }
      }
    },
    { capture: true }
  );
}
